// Payment terms + authorization-to-charge client (design §4.1, §4.3, §4.5, D5).
//
// ─────────────────────────────────────────────────────────────────────────────
// ⚠️ CONTRACT PENDING — the Go lane owns these names, this file only guesses.
//
// The design document deliberately proposes NO identifiers anywhere (its §9,
// rule §82: "sketches state intent, never names"), so the path, the query
// parameter, the response envelope and the field names below are this lane's
// SHAPE, chosen to mirror the one proven precedent in this repo — the referral
// disclosure client (`referral-api.ts`: GET /me/referral-disclosure?kind=…
// returning id / kind / revision / body / effective_from / version).
//
// Everything the wire touches is confined to this file precisely so that
// aligning with the Go lane is a change here and nowhere else. Nothing in the
// components below reads a wire field name directly.
// ─────────────────────────────────────────────────────────────────────────────
//
// FAIL CLOSED, AND THIS CUTS AGAINST THE SURROUNDING CONVENTION (§4.5). The
// embed's other runtime config comes from GET /marketplace/settings/public,
// which is deliberately fail-OPEN: the provider swallows failures and serves
// DEFAULT_STOREFRONT_CONFIG so the storefront never blanks out. That is right
// for a hero headline and wrong for an authorization to charge — a checkout
// that proceeds with default or missing terms records an acceptance of
// nothing. So terms are fetched on their own route, and a failure or an empty
// result BLOCKS checkout rather than degrading.
//
// The disclosure precedent already gets this right and is the model: its
// customer endpoint returns the one active revision and 404s when none is
// active, with the explicit reasoning "refuse, never fall back to an arbitrary
// revision".

import { embedApiFetch } from "../../embed-shared/api-client";

/** ⚠️ CONTRACT PENDING — mirrors the disclosure route's shape. */
const PAYMENT_TERMS_PATH = "/api/v1/marketplace/me/payment-terms";

/**
 * One active terms revision, exactly as displayed.
 *
 * `id` + `revision` are what make an acceptance reconstructable years later
 * (§4.1: "the order records a version reference, never a boolean"). `revision`
 * is the per-kind CONTENT revision and is deliberately distinct from `version`,
 * the rule #10 optimistic-concurrency token — the precedent keeps them separate
 * and so does this.
 *
 * `body` is operator-authored text (D5). It is rendered as supplied; no copy in
 * this codebase substitutes for it or is merged into it.
 */
export interface PaymentTermsDocumentWire {
  /** Revision reference echoed back on submit. Opaque — never parsed. */
  id: string;
  /** Document kind (payment authorization, terms of use, …). */
  kind: string;
  /**
   * Which variant this document is: `one_time`, `recurring`, or `all` (the
   * terms of use, which does not vary — model.PaymentTermsVariantAll). Added
   * to the TS contract in the Item 2 fix (fix wave 2026-09-15) — it was
   * already on the Go wire (`myPaymentTermsDocument.Variant`,
   * customer_handler_payment_terms.go: "which variant the SERVER resolved")
   * but unread here. This is now the ONLY source `CheckoutFlow.tsx` reads
   * for "which variant applies" — never a client-side guess.
   */
  variant: string;
  /** Per-kind content revision. NOT the OCC `version` token. */
  revision: number;
  /** The full legal text as shown. */
  body: string;
  /** RFC3339 — when this text became the terms of record. */
  effective_from: string;
  /** Display heading. Optional: falls back to a humanised `kind`. */
  title?: string;
  /**
   * Operator-configured label for the acceptance control. Optional; the gate
   * falls back to naming the documents shown rather than inventing legal copy
   * of its own.
   */
  acceptance_label?: string;
}

/** ⚠️ CONTRACT PENDING — envelope mirrors `listAddresses`' `{ addresses }`. */
interface PaymentTermsResponse {
  documents: PaymentTermsDocumentWire[];
}

/**
 * The active terms revisions to show for this cart.
 *
 * ⚠️ TAKES NO VARIANT ARGUMENT (Item 2/3 fix, R2 catalog display design, fix
 * wave 2026-09-15, operator ruling) — this used to accept a `TermsVariant`
 * and forward it as a `?variant=` filter whenever the caller's own guess
 * (`payment-terms.ts`'s `deriveTermsVariant`) was not `"unknown"`. That let
 * `CheckoutFlow.tsx`'s client-side guess select WHICH documents this call
 * returned, which could disagree with the cart the server would itself
 * resolve — showing the wrong authorization-to-charge text on a legal/money
 * surface. Item 3's companion server fix (customer_handler_payment_terms.go)
 * makes a supplied `variant` select NOTHING — it is accepted and ignored, so
 * an embed bundle shipped before this fix keeps working against the new
 * server (the two deploy separately); the variant (§4.2: "evaluated
 * server-side from the order's lines") is ALWAYS derived by the server from
 * the customer's active cart, and this function omits the filter to match.
 *
 * `variant` per document (`PaymentTermsDocumentWire.variant` — was already
 * on the Go wire, newly added to this TS contract in the same fix; see that
 * field's own doc comment) is the one place a caller now reads which
 * variant applied: the RESPONSE, never a pre-fetch guess. `CheckoutFlow.tsx`
 * no longer computes one at all.
 *
 * Throws ApiError(404) when a required kind has no active revision — the
 * platform's legal-text configuration is incomplete and checkout must stop.
 * Resolves to an EMPTY array only if the server says there are no documents,
 * which callers must also treat as blocking: "no terms" is not "no terms
 * needed".
 */
export async function getActivePaymentTerms(): Promise<PaymentTermsDocumentWire[]> {
  const result = await embedApiFetch<PaymentTermsResponse>(PAYMENT_TERMS_PATH);
  return result?.documents ?? [];
}

/** Display heading for a document, falling back to a humanised kind. */
export function termsDocumentTitle(doc: PaymentTermsDocumentWire): string {
  if (doc.title && doc.title.trim().length > 0) return doc.title.trim();
  const words = doc.kind.replace(/[_-]+/g, " ").trim();
  return words.length === 0 ? "Terms" : words.charAt(0).toUpperCase() + words.slice(1);
}
